import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "@/stores/auth.store";

/**
 * Gate that requires the current user to have the `admin` role.
 *
 * Builds on the existing zustand auth store (`useAuthStore`) which already
 * exposes `isInitialized`, `isLoading`, `isAuthenticated`, and `isAdmin()`.
 * No new hook needed — we just compose the existing ones with a uniform UX
 * (spinner while checking, redirect to `/` if not admin).
 *
 * Use this for any admin-only route, e.g. the courses admin pages and the
 * existing `/admin/analytics` route (which previously had no role check).
 */
export function RequireAdmin({
  children,
  signInPath = "/",
}: {
  children: React.ReactNode;
  /** Where to redirect when the user is not signed in. Defaults to "/". */
  signInPath?: string;
}) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const checkAuth = useAuthStore((s) => s.checkAuth);

  useEffect(() => {
    if (!isInitialized && !isLoading) {
      checkAuth();
    }
  }, [isInitialized, isLoading, checkAuth]);

  if (!isInitialized || isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={signInPath} state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center px-6">
        <h1 className="text-2xl font-bold text-slate-900">Admins only</h1>
        <p className="text-slate-600">
          You don&apos;t have permission to view this page.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

export default RequireAdmin;
